<script setup>
import { ref, watch, onMounted, inject } from 'vue'
import useAxios from '@/composables/useAxios'
import { useTableStore } from '@/store/table'
import { useMainStore } from '@/store'
import { saveDataToLS, getDataFromLS } from '@/utils'
import {
  SButton,
  SIcon,
  SSelect,
  SDropdown,
  SDropdownItem,
  SText
} from '@n966/soilbox_ui'
import { toDay } from '../helpers'
import { getZone } from '../helpers'
import cloneDeep from 'lodash.clonedeep'
import TableImportModal from '@/components/table/components/table-import/table-import-modal.vue'

const emits = defineEmits(['toolbar-action', 'select-handle', 'clear-table'])

const axios = useAxios()

const store = useTableStore()
const mainStore = useMainStore()
const $confirm = inject('$confirm')
const $notify = inject('$notify')

const actionsItems = [
  {
    id: 1,
    iconName: 'fa-rotate-left',
    tooltipText: 'Отменить'
  },
  {
    id: 2,
    iconName: 'fa-rotate-right',
    tooltipText: 'Повторить'
  },
  {
    id: 3,
    iconName: 'fa-copy',
    tooltipText: 'Копировать'
  },
  {
    id: 4,
    iconName: 'fa-paste',
    tooltipText: 'Вставить'
  }
]
const sheetsActionsHandler = (id) => {
  if (id === 'clear') {
    clearTable()
    return
  }

  emits('toolbar-action', id)
}

const clearTable = () => {
  let message =
    'Вы уверены, что хотите очистить таблицу? Все данные будут удалены без возможности восстановления'
  let title = 'Подтверждение очистки таблицы'

  $confirm(message, title, {
    confirmButtonText: 'Очистить',
    cancelButtonText: 'Отменить',
    confirmButtonClass: 'error'
  })
    .then(() => {
      emits('clear-table')
      store.SET_STATE_FIELD('pointsList', [])
      saveDataToLS('pointsList', [])
    })
    .catch(() => {})
}

const excelExport = () => {
  emits('toolbar-action', 5)
}

const coordinateConversions = [
  { title: 'WGS -> MSK', id: 'WGS-MSK' },
  { title: 'MSK -> WGS', id: 'MSK-WGS' }
]

const stopWatch = watch(
  () => mainStore.isLoading,
  (newVal) => {
    if (!newVal) {
      selectHandle(defaultSelectedValue.value.id)

      stopWatch()
    }
  }
)

let defaultSelectedValue = ref(null)

const selectHandle = (id) => {
  const findedCoord = coordinateConversions.find((coord) => coord.id === id)
  if (findedCoord) {
    store.SET_STATE_FIELD('selectValue', findedCoord)
    defaultSelectedValue.value = findedCoord
    saveDataToLS('selectValue', findedCoord)
  }
  emits('select-handle', id)
}

const screenWidth = ref(window.innerWidth)

onMounted(() => {
  const defaultValue = getDataFromLS('selectValue')

  if (defaultValue) {
    defaultSelectedValue.value = defaultValue
  } else {
    defaultSelectedValue.value = coordinateConversions[0]
  }

  store.SET_STATE_FIELD('selectValue', defaultSelectedValue.value)

  window.addEventListener('resize', () => {
    screenWidth.value = window.innerWidth
  })
})

const isShow = ref(false)

const toggleImportModal = () => {
  isShow.value = !isShow.value
}

const dropdownActions = [
  {
    id: 'import',
    label: 'Импорт из файла',
    divided: true
  },
  {
    id: 'export',
    label: 'Экспорт в xlsx'
  },
  {
    id: 'dxf-layers',
    label: 'Экспорт в dxf (Слои)'
  },
  {
    id: 'dxf-blocks',
    label: 'Экспорт в dxf (Блоки)'
  },
  {
    id: 'kml',
    label: 'Экспорт в kml'
  },
  {
    id: 'gpx',
    label: 'Экспорт в gpx'
  }
]

const dropdownHandler = (id) => {
  switch (id) {
    case 'import':
      toggleImportModal()
      break
    case 'export':
      excelExport()
      break
    case 'kml':
      {
        exportFileRequest('kml', 'wgs')
      }
      break
    case 'gpx':
      {
        exportFileRequest('gpx', 'wgs')
      }
      break
    // case 'kmz':
    //   {
    //     exportFileRequest('kmz', 'wgs')
    //   }
    //   break
    case 'dxf-layers':
      {
        exportFileRequest('dxf-layers', 'msk', true)
      }
      break
    case 'dxf-blocks':
      {
        exportFileRequest('dxf-blocks', 'msk', true)
      }
      break
  }
}

const exportFileRequest = async (url, converter, isDXF) => {
  const filteredData = cloneDeep(store.pointsList).filter(
    (point) => point.id && point.lat && point.lon
  )

  filteredData.forEach((item) => {
    const zone = getZone(item.zone_in)
    if (!zone) return
    item.zone_in = zone.id
  })

  if (isDXF) {
    const noXYData = filteredData.filter((item) => {
      return !item.x || !item.y
    })

    if (noXYData.length) {
      const titles = noXYData.map((item) => item.num)
      $notify({
        message: `Отсутствуют X (север), Y (восток) координаты у номеров: ${titles.join(
          ', '
        )}`,
        type: 'error'
      })
      return
    }
  }

  try {
    const { data } = await axios.put(
      `api/v1/converter/${converter}/to/${url}/export`,
      filteredData
    )
    downloadFile(data, url, isDXF)
  } catch (e) {
    console.log(e)
    $notify({
      message: 'Произошла ошибка при экспорте файла',
      type: 'error'
    })
  }
}

const mapExtension = {
  gpx: 'gpx',
  // kmz: 'kmz',
  kml: 'kml',
  'dxf-blocks': 'dxf',
  'dxf-layers': 'dxf'
}

const mapExtensionBlobType = {
  gpx: 'gpx',
  kml: 'kml',
  // kmz: 'vnd.google-earth.kmz',
  'dxf-blocks': 'dxf',
  'dxf-layers': 'dxf'
}

const downloadFile = (data, extension, isDXF) => {
  const blob = new Blob([data], {
    type: `application/${mapExtensionBlobType[extension]}`
  })

  let url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'

  if (isDXF) {
    a.href = `data:application/octet-stream;base64,${btoa(
      unescape(encodeURIComponent(data))
    )}`
  } else {
    a.href = url
  }

  a.download = `${toDay()}.${mapExtension[extension]}`
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
}

const WIDTH_TO_SHOW_FULL_TOOLBAR = 1500

const collapsedToolbarActions = [
  {
    id: 1,
    icon: 'fa-rotate-left',
    label: 'Отменить',
    closeByClick: false
  },
  {
    id: 2,
    icon: 'fa-rotate-right',
    label: 'Повторить',
    closeByClick: false
  },
  {
    id: 3,
    icon: 'fa-copy',
    label: 'Копировать'
  },
  {
    id: 4,
    icon: 'fa-paste',
    label: 'Вставить',
    divided: true
  },
  {
    id: 'clear',
    icon: 'fa-trash',
    label: 'Очистить таблицу',
    closeByClick: true
  }
]
</script>
<template>
  <div class="sheets-toolbar">
    <div class="sheets-toolbar__wrapper">
      <div class="sheets-toolbar__calculate-direction">
        <s-text>Вид пересчета</s-text>
        <s-select
          :value="defaultSelectedValue?.id"
          @change="selectHandle"
          :options="coordinateConversions"
        />
      </div>

      <div class="sheets-toolbar__right">
        <div
          class="sheets-toolbar__actions"
          v-if="screenWidth > WIDTH_TO_SHOW_FULL_TOOLBAR"
        >
          <div class="sheets-toolbar__sheets-actions">
            <div
              v-for="action in actionsItems"
              :key="action.id"
              class="sheets-toolbar__action"
              @click="sheetsActionsHandler(action.id)"
              v-tooltip.right="action.tooltipText"
            >
              <s-icon
                :name="action.iconName"
                size="xl"
              />
            </div>
          </div>
          <s-button
            class="sheets-toolbar__clear-btn"
            @click="clearTable"
          >
            {{ screenWidth > 1300 ? 'Очистить таблицу' : 'Очистить' }}
          </s-button>
        </div>

        <s-dropdown
          v-else
          min-width="178"
          list
          stickTo="right"
        >
          <s-button icon="screwdriver-wrench" />
          <template #dropdown>
            <s-dropdown-item
              v-for="item in collapsedToolbarActions"
              :id="item?.id"
              :key="item.id"
              :type="item.type"
              :icon="item.icon"
              :label="item.label"
              :divided="item.divided"
              :close-by-click="item?.closeByClick"
              @click="sheetsActionsHandler"
              :centerItem="false"
            />
          </template>
        </s-dropdown>

        <div class="sheets-toolbar__exports">
          <s-dropdown
            min-width="178"
            list
          >
            <s-button icon="file-export">Импорт / Экспорт</s-button>
            <template #dropdown>
              <s-dropdown-item
                v-for="item in dropdownActions"
                :id="item?.id"
                :key="item.id"
                :type="item.type"
                :icon="item.icon"
                :label="item.label"
                :divided="item.divided"
                :close-by-click="item?.closeByClick"
                @click="dropdownHandler"
                :centerItem="false"
              />
            </template>
          </s-dropdown>
        </div>
      </div>
    </div>
  </div>
  <table-import-modal
    @close="toggleImportModal"
    @toggle="toggleImportModal"
    :show-modal="isShow"
  />
</template>
<style lang="scss">
.sheets-toolbar {
  display: flex;
  gap: 24px;
  justify-content: space-between;
  height: 40px;

  &__actions {
    display: flex;
    gap: 1rem;
  }

  &__right {
    display: flex;
    gap: 1.5rem;
  }

  &__calculate-direction {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  &__clear-btn {
    flex-shrink: 0;
    border-radius: 8px !important;
  }

  & .s-select {
    max-width: 140px;
  }

  &__wrapper {
    display: flex;
    gap: 1.5rem;
    justify-content: space-between;
    width: 100%;
  }

  &__exports {
    display: flex;
    gap: 24px;
  }

  & .s-dropdown-item {
    justify-content: start;
  }

  &__sheets-actions {
    display: flex;
    align-items: center;
    border: 1px solid var(--main-bg);
    border-radius: 8px;
  }

  &__action {
    height: 100%;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg);
    cursor: pointer;

    & svg {
      width: 16px;
      height: 16px;
    }

    &:first-child {
      border-radius: 8px 0 0 8px;
    }

    &:last-child {
      border-radius: 0 8px 8px 0;
    }
  }
}
</style>
